import React, { useEffect, useState } from "react";
import { RootState } from "@/redux/store";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// Interfaces/Types
import { IDepartmentChargebackCodes, IOem } from "@/Settings/types/SystemSettings/appendingConfiguration";
import { ISystemSettings } from "@/Settings/types/Common/model";

// API
import { getDepartmentChargebackCodes } from "@/Settings/api/SystemSettings/queries";

// Helpers
import getMappedPositionsMenu from "@/Settings/helpers/SystemSettings/positions/getMappedPositionsMenu";
import handleScroll from "@/Settings/helpers/SystemSettings/handleScroll";
import { resolveOems } from "@/Settings/helpers/SystemSettings/appendingConfiguration";

// Layouts
import CollapseArea from "@/Layouts/components/Basics/CollapseArea";

// MUI
import { Box, Grid } from "@mui/material";

// Common Components
import CommonDivider from "../common/CommonDivider";
import SystemSettingsLoader from "../SystemSettingsLoader";
import SystemSettingsSideBar from "../SystemSettingsSideBar";

// Sections
import ModalitiesConfiguration from "./sections/ModalitiesConfiguration";
import GlobalRanking from "./sections/GlobalRanking";
import ChargeBackLimits from "./sections/ChargeBackLimits";
import ChargeBackTypes from "./sections/ChargeBackTypes";
import DefaultPayPlans from "./sections/DefaultPayPlans";
import AppendingConfiguration from "./sections/AppendingConfiguration";
import ChargebacksAccountReferences from "./sections/ChargebacksAccountReferences";
import AdvisorsRankingConfiguration from "./sections/AdvisorsRankingConfiguration";
import { SALES_POSITIONS } from "@/Settings/Constants/SystemSettings";

type TPositionFragmentProps = {
	menu: {
		label: string;
		elementId: string;
		sections: string[];
		isDepartmental: boolean;
	};
	locationId: string;
	systemSettings: ISystemSettings | null;
	mappedChargebacks: IDepartmentChargebackCodes[];
	onHandleCollapse: (collapsed: boolean, selected: string) => void;
	itemSelected: string;
	index: number;
	oems: IOem[];
};

const checkAccess = (
	menu: {
		label: string;
		elementId: string;
		sections: string[];
		isDepartmental: boolean;
	},
	section: string,
	wasOpen: boolean
) => {
	return menu.sections.includes(section) && wasOpen;
};

const getRelatedChargebackAccounts = (systemSettings: ISystemSettings | null, position = "") => {
	const { chargebackAccountReferences = [] } = systemSettings || {};
	const reference = chargebackAccountReferences?.find((chargeback) => chargeback.position === position);
	return reference?.chargebackTypes ?? [];
};

const PositionFragment = (props: TPositionFragmentProps) => {
	const { menu, locationId, systemSettings, mappedChargebacks, onHandleCollapse, itemSelected, index, oems } = props;
	const [wasOpen, setWasOpen] = useState(index === 0);
	const openCollapse = itemSelected === menu.elementId;

	const handleCollapse = (collapsed: boolean) => {
		setWasOpen(true);
		onHandleCollapse(collapsed, menu.elementId);
	};

	useEffect(() => {
		if (openCollapse) {
			setWasOpen(true);
		}
	}, [openCollapse]);

	return (
		<CollapseArea
			title={menu.label}
			openCollapse={openCollapse}
			collapsible
			onToggleCollapse={handleCollapse}
			sx={{ padding: "0px 30px" }}
		>
			<Box>
				{checkAccess(menu, "DefaultPayPlan", wasOpen) && (
					<DefaultPayPlans
						position={menu.label}
						elementId={menu.elementId}
						locationId={locationId}
						mappedPositions={systemSettings?.mappedPositions}
						defaultPayPlans={systemSettings?.defaultPayPlans}
					/>
				)}
				{checkAccess(menu, "ModalitiesConfiguration", wasOpen) && (
					<>
						<CommonDivider className="!ml-0" />
						<ModalitiesConfiguration
							position={menu.label}
							locationId={locationId}
							mappedPositions={systemSettings?.mappedPositions}
							workingDays={systemSettings?.workingDays}
							drawModalities={systemSettings?.drawModalities}
							payplanTermConditions={systemSettings?.payplanTermConditions}
						/>
					</>
				)}
				{checkAccess(menu, "GlobalRanking", wasOpen) && (
					<>
						<CommonDivider className="!ml-0" />
						<GlobalRanking locationId={locationId} isGlobalRanking={systemSettings?.isGlobalRanking} />
					</>
				)}
				{checkAccess(menu, "ChargebackTypes", wasOpen) && (
					<>
						<CommonDivider className="!ml-0" />
						<ChargeBackTypes
							locationId={locationId}
							chargebackTypes={systemSettings?.chargebackTypes?.find((chargeback) => chargeback.position === menu.label)}
							mappedPayItems={systemSettings?.mappedPayItems}
							positionChargebacksCodes={mappedChargebacks?.filter((chargeback: { department: string }) => {
								const isSalesDepartment = SALES_POSITIONS.includes(menu.label);
								return isSalesDepartment ? chargeback.department === "sales" : chargeback.department === "service";
							})}
							position={menu.label}
							isDepartmental={menu.isDepartmental}
						/>
					</>
				)}
				{checkAccess(menu, "AdvisorsRankingConfiguration", wasOpen) && (
					<>
						<CommonDivider className="!ml-0" />
						<AdvisorsRankingConfiguration
							locationId={locationId}
							rankingConfiguration={systemSettings?.advisorsRankingConfiguration ?? null}
						/>
					</>
				)}
				{checkAccess(menu, "ChargebacksAccountReferences", wasOpen) && (
					<>
						<CommonDivider className="!ml-0" />
						<ChargebacksAccountReferences
							locationId={locationId}
							position={menu.label}
							chargebacksReferences={getRelatedChargebackAccounts(systemSettings, menu.label)}
							chargebackAccountReferences={systemSettings?.chargebackAccountReferences ?? []}
						/>
					</>
				)}
				{checkAccess(menu, "ChargebackLimits", wasOpen) && (
					<>
						<CommonDivider className="!ml-0" />
						<ChargeBackLimits
							locationId={locationId}
							chargebackLimits={systemSettings?.chargebackLimits}
							position={menu.label}
						/>
					</>
				)}
				{checkAccess(menu, "AppendingConfiguration", wasOpen) && (
					<>
						<CommonDivider className="!ml-0" />
						<AppendingConfiguration
							locationId={locationId}
							appendingConfiguration={systemSettings?.appendingConfiguration?.filter((item) =>
								item.position.startsWith(menu.label)
							)}
							position={menu.label}
							isDepartmental={menu.isDepartmental}
							oems={oems}
							openCollapse={openCollapse}
						/>
					</>
				)}
			</Box>
		</CollapseArea>
	);
};

const PositionsContainer = ({ selected }: { selected: number }) => {
	const isPositionsSelected = selected === 2;
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();
	const isLoadingSystemSettings = queryClient.getQueryState(["systemSettings", locationId])?.status === "pending";
	const isLoadingSystemSettingsAdditionalInfo =
		queryClient.getQueryState(["systemSettingsAdditionalInfo", locationId])?.status === "pending";
	const isLoadingAppendingConfigInfo = queryClient.getQueryState(["appendingConfigInfo", locationId])?.status === "pending";
	const systemSettings = queryClient.getQueryData(["systemSettings", locationId]) as ISystemSettings | null;
	const oems = resolveOems();
	const menus = getMappedPositionsMenu(systemSettings?.mappedPositions);
	const menusLengthMinusOne = menus.length - 1;

	const [itemSelected, setItemSelected] = useState(menus[0]?.elementId || "");

	const handleMenuClick = (param: { label: string; elementId: string }) => {
		setItemSelected(param?.elementId);
		handleScroll(param?.elementId, 1000);
	};

	const handleCollapse = (collapsed: boolean, selection: string) => {
		if (!collapsed) {
			setItemSelected(selection);
			handleScroll(selection, 1000);
		}
	};

	const positions = menus.filter((menu) => menu.sections.includes("ChargebackTypes")).map((menu) => menu.label);

	const { data: mappedChargebacks, isLoading: isLoadingChargebacks } = useQuery({
		queryKey: ["getDepartmentChargebackCodes", locationId, positions],
		queryFn: async () => getDepartmentChargebackCodes(),
		enabled: !!locationId && !!positions.length,
		staleTime: Infinity,
	});

	const isLoading =
		isLoadingSystemSettings || isLoadingSystemSettingsAdditionalInfo || isLoadingChargebacks || isLoadingAppendingConfigInfo;

	return (
		<Grid container className="my-[30px]">
			<Grid item xs={2}>
				<SystemSettingsSideBar menus={menus} onActionClick={handleMenuClick} selected={itemSelected} loading={isLoading} />
			</Grid>
			<Grid item xs={10}>
				<Grid className="!min-h-600px w-full ml-[30px]">
					{isLoading || !locationId ? (
						<SystemSettingsLoader />
					) : (
						<>
							{isPositionsSelected &&
								menus.map((menu, index) => {
									return (
										<React.Fragment key={menu.elementId}>
											<Box id={menu.elementId}>
												<PositionFragment
													menu={menu}
													locationId={locationId}
													systemSettings={systemSettings}
													mappedChargebacks={mappedChargebacks}
													onHandleCollapse={handleCollapse}
													itemSelected={itemSelected}
													index={index}
													oems={oems}
												/>
											</Box>
											{index !== menusLengthMinusOne && <CommonDivider />}
										</React.Fragment>
									);
								})}
						</>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default PositionsContainer;
